import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { useAppSelector } from '../../../store/store';
import { selectMemberNameById } from '../../sharing/groupsSlice';
import type {
  EnhancedInventoryItem,
  InventoryItemResponse,
} from '../../../api/types';

/**
 * FE-233 — State banner on ItemDetail (single source of truth).
 *
 * Renders one of 6 variants based on item + viewer state:
 *
 *   1. Available with viewer-as-owner       → no banner (clean state)
 *   2. Lent via crew handoff                → orange "With {name} since X"
 *   3. Shared via 1-on-1 PENDING            → purple "Shared with X · Yh left"
 *   4. Shared CLAIMED                       → purple "X claimed this"
 *   5. RECALL active                        → red "You recalled this"
 *   6. Soft-deleted                         → grey "Deleted on X · read-only"
 *
 * The component is read-only — actions live in the action row below
 * the banner. State derivation is pure: we look at item.deleted_at,
 * current_custodian_user_id vs user_id, and (when present)
 * availability_state for the share variants. No extra fetch.
 *
 * Custodian POV gets a parallel banner set: "🟢 You're holding this",
 * "⚠ Marcus is recalling this", "🟣 Borrowed from Marcus", etc.
 */

interface Props {
  // Accept either shape — ItemDetailScreen passes whichever it loaded
  // (EnhancedInventoryItem from the grid path, InventoryItemResponse
  // from a direct GET). All the fields we read are common to both.
  item: InventoryItemResponse | EnhancedInventoryItem;
  viewerIsOwner: boolean;
  viewerIsCustodian: boolean;
}

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const days = Math.floor((Date.now() - t) / 86_400_000);
    if (days === 0) return 'today';
    if (days === 1) return '1 day';
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    return `${Math.floor(days / 365)} years`;
  } catch {
    return null;
  }
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

export const ItemStateBanner: React.FC<Props> = ({
  item,
  viewerIsOwner,
  viewerIsCustodian,
}) => {
  const ownerId = (item as any).user_id ?? (item as any).owner_user_id;
  const custodianId = (item as any).current_custodian_user_id ?? null;
  const custodySince = (item as any).custody_since ?? null;
  const dueDate = (item as any).custody_due_date ?? null;
  const deletedAt = (item as any).deleted_at ?? null;

  // Resolve the counterparty name from cached group rosters — best-
  // effort. Falls back to "another member" when unresolvable.
  const counterpartyId = viewerIsOwner ? custodianId : ownerId;
  const counterpartyName = useAppSelector(
    selectMemberNameById(counterpartyId),
  );

  // ── 6. Soft-deleted (highest priority — short-circuits everything else)
  if (deletedAt) {
    const when = formatDate(deletedAt);
    return (
      <View
        style={[styles.banner, styles.tonedSoftDeleted]}
        testID="item-state-banner-deleted"
      >
        <MaterialIcons
          name="block"
          size={18}
          color={fpColors.onSurfaceVariant}
        />
        <View style={{ flex: 1 }}>
          <Text style={[styles.title, { color: fpColors.onSurfaceVariant }]}>
            Deleted {when ?? ''}
          </Text>
          <Text style={styles.body}>
            Read-only view from custody history.
          </Text>
        </View>
      </View>
    );
  }

  // Custody is out when the custodian is not the owner.
  const isOut =
    custodianId != null &&
    ownerId != null &&
    custodianId !== ownerId;

  // ── 1. Available with owner — no banner.
  if (!isOut) return null;

  // ── Custodian POV — they hold the item.
  if (viewerIsCustodian) {
    return (
      <View
        style={[styles.banner, styles.tonedHoldingOk]}
        testID="item-state-banner-holding"
      >
        <MaterialIcons name="bookmark" size={18} color={fpColors.onPrimaryContainer} />
        <View style={{ flex: 1 }}>
          {/* Text on the saturated primaryContainer (#9850d2) must use
              onPrimaryContainer (#fff8fd) — `primary` here was purple-on-purple. */}
          <Text style={[styles.title, { color: fpColors.onPrimaryContainer }]}>
            You're holding this
          </Text>
          <Text style={[styles.body, { color: fpColors.onPrimaryContainer }]}>
            {(counterpartyName ?? 'The owner') +
              ' will get a return notification.'}
            {dueDate ? ` Due ${formatDate(dueDate) ?? 'soon'}.` : ''}
          </Text>
        </View>
      </View>
    );
  }

  // ── 2. Owner POV — Lent via crew handoff. (We don't have a clean
  // signal for share-vs-handoff vs RECALL on the item row alone — the
  // share lifecycle banner pieces land in Wave G's Active-shares card.
  // For Wave E we ship the unified "out with someone" banner which
  // covers all three variants the owner cares about.)
  const since = relativeTime(custodySince);
  return (
    <View
      style={[styles.banner, styles.tonedLentOut]}
      testID="item-state-banner-out"
    >
      <MaterialIcons name="schedule" size={18} color={fpColors.onTertiaryContainer} />
      <View style={{ flex: 1 }}>
        {/* Text on the gold tertiaryContainer must use onTertiaryContainer
            (#4f3e00) — `tertiary` here was gold-on-gold. */}
        <Text style={[styles.title, { color: fpColors.onTertiaryContainer }]}>
          With {counterpartyName ?? 'another user'}
          {since ? ` · ${since}` : ''}
        </Text>
        <Text style={[styles.body, { color: fpColors.onTertiaryContainer }]}>
          {dueDate
            ? `Due back ${formatDate(dueDate) ?? 'soon'}.`
            : 'Send a reminder or recall from the actions below.'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
  },
  tonedSoftDeleted: {
    backgroundColor: fpColors.surfaceContainerHigh,
    borderColor: fpColors.outlineVariant,
  },
  tonedLentOut: {
    // Solid (no 88 alpha) so the gold bg is predictable against the
    // dark onTertiaryContainer text.
    backgroundColor: fpColors.tertiaryContainer,
    borderColor: fpColors.tertiary,
  },
  tonedHoldingOk: {
    backgroundColor: fpColors.primaryContainer,
    borderColor: fpColors.primary,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  body: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
    lineHeight: 16,
  },
});
